/**
 * Modelo de presentación de las notas: convierte el contenido de cada
 * plantilla (SDD §9.5) en secciones ordenadas. Lo usan la vista, la edición,
 * el Markdown y el PDF, así que el orden y los títulos viven en un solo lugar.
 */
import {
  TEMPLATES,
  type ActionItem,
  type Evidence,
  type Summary,
  type TemplateId
} from '@eco/shared'

export interface ListItem {
  texto: string
  evidencia: Evidence[]
}

export type NoteSection =
  | { kind: 'text'; key: string; title: string; text: string }
  | { kind: 'list'; key: string; title: string; items: ListItem[] }
  | { kind: 'tasks'; key: 'actionItems'; title: string; items: ActionItem[] }
  | { kind: 'flag'; key: string; title: string; value: string; label: string }

type Field =
  | { key: string; title: string; kind: 'text' }
  | { key: string; title: string; kind: 'strings' } // string[]
  | { key: string; title: string; kind: 'items' } // {texto, evidencia}[]
  | { key: 'actionItems'; title: string; kind: 'tasks' }
  | { key: string; title: string; kind: 'flag'; labels: Record<string, string> }

const LAYOUT: Record<Exclude<TemplateId, 'personalizada'>, Field[]> = {
  reunion_general: [
    { key: 'resumen', title: 'Resumen', kind: 'text' },
    { key: 'puntosClave', title: 'Puntos clave', kind: 'strings' },
    { key: 'decisiones', title: 'Decisiones', kind: 'items' },
    { key: 'actionItems', title: 'Tareas', kind: 'tasks' },
    { key: 'temas', title: 'Temas', kind: 'strings' },
    { key: 'preguntasAbiertas', title: 'Preguntas abiertas', kind: 'strings' }
  ],
  entrevista: [
    { key: 'resumen', title: 'Resumen', kind: 'text' },
    { key: 'perfilResumen', title: 'Perfil', kind: 'text' },
    {
      key: 'consentimientoConfirmado',
      title: 'Consentimiento en la conversación',
      kind: 'flag',
      labels: { si: 'Consta que se avisó', no: 'Consta que no se avisó', no_consta: 'No consta' }
    },
    { key: 'fortalezas', title: 'Fortalezas', kind: 'items' },
    { key: 'puntosAVerificar', title: 'Puntos a verificar', kind: 'items' },
    { key: 'puntosClave', title: 'Puntos clave', kind: 'strings' },
    { key: 'actionItems', title: 'Tareas', kind: 'tasks' },
    { key: 'siguientesPasos', title: 'Siguientes pasos', kind: 'strings' }
  ],
  reunion_cliente: [
    { key: 'resumen', title: 'Resumen', kind: 'text' },
    { key: 'contexto', title: 'Contexto', kind: 'text' },
    { key: 'necesidades', title: 'Necesidades', kind: 'items' },
    { key: 'compromisos', title: 'Compromisos', kind: 'items' },
    { key: 'riesgos', title: 'Riesgos', kind: 'items' },
    { key: 'puntosClave', title: 'Puntos clave', kind: 'strings' },
    { key: 'actionItems', title: 'Tareas', kind: 'tasks' }
  ]
}

type Obj = Record<string, unknown>

/** Copia de la plantilla personalizada guardada dentro de las notas. */
interface PlantillaSnapshot {
  nombre: string
  secciones: { key: string; titulo: string }[]
}

export function customSnapshot(contenido: unknown): PlantillaSnapshot | null {
  const p = (contenido as Obj | null)?._plantilla as PlantillaSnapshot | undefined
  if (!p || typeof p.nombre !== 'string' || !Array.isArray(p.secciones)) return null
  return p
}

/** Campos de una plantilla; los de una personalizada salen de la copia dentro de las notas. */
function layoutFor(templateId: TemplateId, contenido: unknown): Field[] {
  if (templateId !== 'personalizada') return LAYOUT[templateId] ?? LAYOUT.reunion_general
  const secciones = customSnapshot(contenido)?.secciones ?? []
  return [
    { key: 'resumen', title: 'Resumen', kind: 'text' },
    ...secciones
      .filter((s) => /^seccion_\d+$/.test(s.key))
      .map((s) => ({ key: s.key, title: s.titulo, kind: 'items' as const })),
    { key: 'puntosClave', title: 'Puntos clave', kind: 'strings' },
    { key: 'actionItems', title: 'Tareas', kind: 'tasks' }
  ]
}

/** Nombre visible de unas notas: el de la plantilla de eco o el de la personalizada. */
export function templateName(s: Pick<Summary, 'templateId' | 'contenido'>): string {
  if (s.templateId === 'personalizada')
    return customSnapshot(s.contenido)?.nombre ?? 'Personalizada'
  return TEMPLATES[s.templateId]?.nombre ?? 'Notas'
}
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])
const str = (v: unknown): string => (typeof v === 'string' ? v : '')
function evid(v: unknown): Evidence[] {
  return arr(v).filter(
    (e): e is Evidence =>
      typeof e === 'object' && e !== null && typeof (e as Evidence).inicioMs === 'number'
  )
}

/**
 * Secciones visibles (las vacías se omiten salvo el resumen). Con `nameOf`, cada
 * «Participante N» del texto (resumen, puntos, decisiones, tareas) se muestra con
 * el nombre que el usuario le puso a esa voz: las notas se escriben con las
 * etiquetas estables y los nombres se pueden cambiar después.
 */
export function noteSections(
  templateId: TemplateId,
  contenido: unknown,
  nameOf?: (etiqueta: string) => string
): NoteSection[] {
  const c = (contenido ?? {}) as Obj
  const out: NoteSection[] = []
  const nombrar = (t: string) => (nameOf ? t.replace(/\bParticipante \d+\b/g, (m) => nameOf(m)) : t)
  for (const f of layoutFor(templateId, contenido)) {
    const v = c[f.key]
    switch (f.kind) {
      case 'text': {
        const text = nombrar(str(v).trim())
        if (text || f.key === 'resumen')
          out.push({ kind: 'text', key: f.key, title: f.title, text })
        break
      }
      case 'strings': {
        const items = arr(v)
          .map((x) => ({ texto: nombrar(str(x)), evidencia: [] }))
          .filter((x) => x.texto)
        if (items.length) out.push({ kind: 'list', key: f.key, title: f.title, items })
        break
      }
      case 'items': {
        const items = arr(v)
          .map((x) => ({
            texto: nombrar(str((x as Obj)?.texto)),
            evidencia: evid((x as Obj)?.evidencia)
          }))
          .filter((x) => x.texto)
        if (items.length) out.push({ kind: 'list', key: f.key, title: f.title, items })
        break
      }
      case 'tasks': {
        const items = arr(v)
          .filter((x): x is ActionItem => typeof (x as ActionItem)?.descripcion === 'string')
          .map((t) => ({ ...t, descripcion: nombrar(t.descripcion) }))
        if (items.length) out.push({ kind: 'tasks', key: 'actionItems', title: f.title, items })
        break
      }
      case 'flag': {
        const value = str(v)
        if (value)
          out.push({
            kind: 'flag',
            key: f.key,
            title: f.title,
            value,
            label: f.labels[value] ?? value
          })
        break
      }
    }
  }
  return out
}

/** Campos editables de una plantilla, incluidas las secciones vacías. */
export function editableFields(templateId: TemplateId, contenido?: unknown): Field[] {
  return layoutFor(templateId, contenido)
}

/** Responsable de una tarea para mostrar: nombre dicho, o el nombre del hablante. */
export function taskOwner(
  a: Pick<ActionItem, 'responsableEtiqueta' | 'responsableMencionado'> & { descripcion?: string },
  displayName: (etiqueta: string) => string = (e) => e
): string | null {
  // Un nombre que aparece en la propia tarea («Escribir a Marcela») es a quien va
  // dirigida, no quien la hace: manda el hablante que se comprometió
  const enLaTarea =
    !!a.responsableMencionado &&
    !!a.descripcion?.toLowerCase().includes(a.responsableMencionado.toLowerCase())
  if (a.responsableMencionado && !(enLaTarea && a.responsableEtiqueta))
    return a.responsableMencionado
  if (a.responsableEtiqueta) return displayName(a.responsableEtiqueta)
  return null
}

// ---------------------------------------------------------------------------
// Edición: operaciones puras sobre el contenido (la API valida el esquema)
// ---------------------------------------------------------------------------

export function setText(contenido: unknown, key: string, text: string): Obj {
  return { ...(contenido as Obj), [key]: text }
}

/**
 * Reemplaza una lista a partir de líneas de texto. Conserva la evidencia de
 * las líneas que no cambiaron (por texto), para no perder las citas al editar.
 */
export function setLines(
  templateId: TemplateId,
  contenido: unknown,
  key: string,
  lines: string[]
): Obj {
  const c = contenido as Obj
  const field = editableFields(templateId, contenido).find((f) => f.key === key)
  const clean = lines.map((l) => l.trim()).filter(Boolean)
  if (field?.kind === 'items') {
    const prev = new Map(
      arr(c[key]).map((x) => [str((x as Obj).texto), evid((x as Obj).evidencia)] as const)
    )
    return { ...c, [key]: clean.map((texto) => ({ texto, evidencia: prev.get(texto) ?? [] })) }
  }
  return { ...c, [key]: clean }
}

export function setTasks(contenido: unknown, tasks: ActionItem[]): Obj {
  return {
    ...(contenido as Obj),
    actionItems: tasks
      .filter((t) => t.descripcion.trim())
      .map(({ sinEvidencia: _omit, ...t }) => ({ ...t, descripcion: t.descripcion.trim() }))
  }
}

export function linesOf(contenido: unknown, key: string): string[] {
  return arr((contenido as Obj)?.[key]).map((x) =>
    typeof x === 'string' ? x : str((x as Obj)?.texto)
  )
}

export function tasksOf(contenido: unknown): ActionItem[] {
  return arr((contenido as Obj)?.actionItems).filter(
    (x): x is ActionItem => typeof (x as ActionItem)?.descripcion === 'string'
  )
}
